import { useState, useEffect } from "react";
import { ArrowLeft, Plus, Edit, Trash2, Clock } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Input } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/input";
import { Label } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/projects/Admin_Bite_Balance/bite-balance/src/hooks/use-toast";

interface MedicationDose {
  time: string;
  frequency: string;
}

interface Medication {
  id: string;
  name: string;
  doses: MedicationDose[];
  active: boolean;
}

const MedicationManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [medications, setMedications] = useState<Medication[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMed, setEditingMed] = useState<Medication | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    doses: [{ time: "", frequency: "" }],
  });

  useEffect(() => {
    const stored = localStorage.getItem("medications");
    if (stored) {
      setMedications(JSON.parse(stored));
    }
  }, []);

  const saveMedications = (meds: Medication[]) => {
    localStorage.setItem("medications", JSON.stringify(meds));
    setMedications(meds);
  };

  const handleAddDose = () => {
    setFormData({
      ...formData,
      doses: [...formData.doses, { time: "", frequency: "" }],
    });
  };

  const handleRemoveDose = (index: number) => {
    const newDoses = formData.doses.filter((_, i) => i !== index);
    setFormData({ ...formData, doses: newDoses });
  };

  const handleDoseChange = (
    index: number,
    field: keyof MedicationDose,
    value: string
  ) => {
    const newDoses = [...formData.doses];
    newDoses[index][field] = value;
    setFormData({ ...formData, doses: newDoses });
  };

  const handleSubmit = () => {
    if (!formData.name || formData.doses.some(d => !d.time || !d.frequency)) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    if (editingMed) {
      const updated = medications.map(m =>
        m.id === editingMed.id
          ? { ...m, name: formData.name, doses: formData.doses }
          : m
      );
      saveMedications(updated);
      toast({
        title: "Medication Updated",
        description: "Your medication has been updated successfully",
      });
    } else {
      const newMed: Medication = {
        id: Date.now().toString(),
        name: formData.name,
        doses: formData.doses,
        active: true,
      };
      saveMedications([...medications, newMed]);
      toast({
        title: "Medication Added",
        description: "Your medication has been added successfully",
      });
    }

    setIsDialogOpen(false);
    setEditingMed(null);
    setFormData({ name: "", doses: [{ time: "", frequency: "" }] });
  };

  const handleEdit = (med: Medication) => {
    setEditingMed(med);
    setFormData({
      name: med.name,
      doses: [...med.doses],
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    const updated = medications.filter(m => m.id !== id);
    saveMedications(updated);
    toast({
      title: "Medication Deleted",
      description: "Your medication has been removed",
    });
  };

  const handleToggleActive = (id: string) => {
    const updated = medications.map(m =>
      m.id === id ? { ...m, active: !m.active } : m
    );
    saveMedications(updated);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border/50 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">
              Medication Management
            </h1>
            <p className="text-xs text-muted-foreground">
              Track your medications
            </p>
          </div>
          <Button
            size="sm"
            className="rounded-lg"
            onClick={() => {
              setEditingMed(null);
              setFormData({ name: "", doses: [{ time: "", frequency: "" }] });
              setIsDialogOpen(true);
            }}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
        </div>

        <div className="space-y-3 p-4">
          {medications.length === 0 ? (
            <Card className="rounded-2xl border-2 border-dashed border-border p-8 text-center">
              <p className="text-muted-foreground">No medications added yet</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Click "Add" to create your first medication reminder
              </p>
            </Card>
          ) : (
            medications.map(med => (
              <Card
                key={med.id}
                className="rounded-2xl border-2 border-border p-5 shadow-sm"
              >
                <div className="mb-3 flex items-start gap-3">
                  <div
                    className={`mt-1.5 h-3 w-3 flex-shrink-0 cursor-pointer rounded-full ${
                      med.active ? "bg-secondary" : "bg-muted"
                    }`}
                    onClick={() => handleToggleActive(med.id)}
                  />
                  <div className="flex-1">
                    <h3 className="mb-1 font-semibold text-foreground">
                      {med.name}
                    </h3>

                    <div className="mt-2 space-y-2">
                      {med.doses.map((dose, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 rounded-lg bg-muted/30 p-2 text-xs"
                        >
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span className="text-muted-foreground">
                            {dose.frequency}
                          </span>
                          <span className="text-muted-foreground">•</span>
                          <span className="font-medium text-foreground">
                            {dose.time}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={() => handleEdit(med)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-destructive"
                      onClick={() => handleDelete(med.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingMed ? "Edit Medication" : "Add Medication"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Medication Name</Label>
              <Input
                value={formData.name}
                onChange={e =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Vitamin D"
                className="mt-1.5"
              />
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Daily Doses</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddDose}
                  className="h-7 text-xs"
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Add Dose
                </Button>
              </div>

              <div className="space-y-3">
                {formData.doses.map((dose, index) => (
                  <Card key={index} className="border-2 p-3">
                    <div className="space-y-2">
                      <div className="mb-2 flex items-center justify-between">
                        <Label className="text-xs">Dose {index + 1}</Label>
                        {formData.doses.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveDose(index)}
                            className="h-6 w-6 p-0 text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>

                      <div>
                        <Label className="text-xs">Frequency</Label>
                        <Input
                          value={dose.frequency}
                          onChange={e =>
                            handleDoseChange(index, "frequency", e.target.value)
                          }
                          placeholder="e.g., With breakfast"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label className="text-xs">Time</Label>
                        <Input
                          type="time"
                          value={dose.time}
                          onChange={e =>
                            handleDoseChange(index, "time", e.target.value)
                          }
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingMed ? "Update" : "Add"} Medication
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicationManagement;
